import * as React from "react";
import {
  Typography,
  Paper,
  IconButton,
  InputBase,
  Box,
  Container,
  Pagination,
  MenuItem,
  Button,
  Grid,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Select,
  FormHelperText,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  TextField,
} from "@mui/material";
import { connect } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import {
  centered_flex_box,
  MainInput,
  MainInputLabel,
  main_button,
  sec_button,
  StyledInput,
} from "../../app/components/Styles";
import moment from "moment";
import { RequestCard } from "../../app/components";
import {
  acceptTicketRequest,
  fulfillTicketRequest,
  generateRequestDetails,
  getTicketRequestsAdmin,
  rejectTicketRequest,
} from "../../app/store/actions/adminActions";
import { uploadFile } from "react-s3";
import { config } from "../../app/config";
import { payTicketRequest } from "../../app/store/actions/userActions";
import { useParams } from "react-router-dom";
import ActionDialog from "../../app/components/ActionDialog";
import RequestRow from "./components/RequestRow";
import { motion } from "framer-motion";
import { notification } from "antd";
import {
  PlatformPage,
  PlatformPageHeader,
  PlatformCard,
  PlatformButton,
  PlatformEmptyState,
  PlatformLoadingBlock,
} from "../../app/components/platform";
import {
  platformColors,
  platformFilterPaperSx,
  platformFilterSelectSx,
  platformPrimaryButtonSx,
  platformSearchInputSx,
  platformTableSx,
  platformTableContainerSx,
} from "../../app/components/tickitPlatformTheme";

export const TicketRequests = ({
  token,
  requests,
  isLoading,
  getTicketRequestsAdmin,
  generateRequestDetails,
  acceptTicketRequest,
  fulfillTicketRequest,
  rejectTicketRequest,
}) => {
  const eventId = useParams().id;
  const role = token.split(" ")[0];

  const initialState = {
    status: "Unseen",
    search: "",
    eventId,
    page: 1,
    limit: role === "Admin" || role === "Super" ? 50 : 10,
  };

  const [action, setAction] = React.useState(false);

  React.useEffect(() => {
    getTicketRequestsAdmin(formData);
  }, [action]);

  React.useEffect(() => {
    notification.config({ top: "10vh" });
  }, []);

  const [formData, setFormData] = React.useState(initialState);

  const handleClearFilter = (event) => {
    setFormData(initialState);
    getTicketRequestsAdmin(initialState);
  };

  const { status, search, page } = formData;

  const [dialogs, setDialogs] = React.useState({});

  const handlePageChange = (event, value) => {
    setFormData({ ...formData, page: value });
    getTicketRequestsAdmin({ ...formData, page: value });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSearchFilter = (event) => {
    getTicketRequestsAdmin(formData);
  };

  const statusSelectSx = platformFilterSelectSx;

  return (
    <PlatformPage>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <PlatformPageHeader
          title="Ticket requests"
          subtitle={eventId ? "Filter and manage requests for this event" : "Filter by status and search ticket requests"}
        />

        <PlatformCard title="Filters" sx={{ mb: 2.5 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  label="Status"
                  name="status"
                  onChange={handleChange}
                  sx={statusSelectSx}
                >
                  <MenuItem value={"Accepted"} sx={{ color: "#1f1f1f" }}>Accepted</MenuItem>
                  <MenuItem value={"Rejected"} sx={{ color: "#1f1f1f" }}>Rejected</MenuItem>
                  <MenuItem value={"Unseen"}>Pending</MenuItem>
                  <MenuItem value={"Purchased"}>Purchased</MenuItem>
                  <MenuItem value={"Any"}>Any</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={5}>
              <FormControl fullWidth>
                <OutlinedInput
                  id="search"
                  name="search"
                  value={search}
                  onChange={handleChange}
                  placeholder="Search for Event Requests"
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton onClick={handleSearchFilter} edge="end">
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  }
                  sx={platformSearchInputSx}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="flex-end">
                <PlatformButton variant="ghost" onClick={handleClearFilter}>
                  Clear
                </PlatformButton>
                <PlatformButton variant="primary" onClick={handleSearchFilter}>
                  Search
                </PlatformButton>
              </Stack>
            </Grid>
          </Grid>
        </PlatformCard>

        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 2, sm: 3 }, 
            ...platformFilterPaperSx,
            mb: 0,
          }}
        >
          {eventId && (
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontFamily: "'Syne', TickitOne, Helvetica, Arial, sans-serif", fontWeight: 800, fontSize: "1.125rem", color: 'var(--platform-text)', textAlign: "center" }}>
                Event requests
              </Typography>
              {requests?.docs?.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={() => generateRequestDetails({ status, eventId })}
                    sx={platformPrimaryButtonSx}
                  >
                    Download ({requests?.total}) {status} requests
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {!isLoading ? (
            role === "User" ? (
              <Grid container spacing={3}>
                {requests?.docs?.map((request) => (
                  <Grid item xs={12} sm={6} key={request._id}>
                    <RequestCard
                      request={request}
                      action={action}
                      setAction={setAction}
                    />
                  </Grid>
                ))}
                {requests?.docs?.length === 0 && (
                  <PlatformEmptyState title="No requests found" description="Try adjusting your filters." />
                )}
              </Grid>
            ) : (
              <TableContainer sx={platformTableContainerSx}>
                <Table sx={{ minWidth: 650, ...platformTableSx }} aria-label="requests table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Mobile</TableCell>
                      <TableCell>Deadline</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requests?.docs?.map((request) => (
                      <RequestRow 
                        key={request._id}
                        request={request}
                        action={action}
                        setAction={setAction}
                      />
                    ))}
                  </TableBody>
                </Table>
                {requests?.docs?.length === 0 && (
                  <PlatformEmptyState title="No requests found" description="Try adjusting your filters." />
                )}
              </TableContainer>
            )
          ) : (
            <PlatformLoadingBlock />
          )}
          <Box sx={{ ...centered_flex_box, mt: 3 }}>
            <Pagination
              count={requests?.pages || 1}
              page={page}
              onChange={handlePageChange}
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: 1,
                },
                '& .Mui-selected': {
                  backgroundColor: `${platformColors.brandPale} !important`,
                  color: platformColors.brand,
                },
              }}
            />
          </Box>
        </Paper>
      </motion.div>
    </PlatformPage>
  );
};

const mapStateToProps = (state) => ({
  token: state?.auth?.token,
  requests: state?.records?.results?.requests,
  isLoading: state?.records?.isLoading,
});

const mapDispatchToProps = {
  getTicketRequestsAdmin,
  generateRequestDetails,
  acceptTicketRequest,
  fulfillTicketRequest,
  rejectTicketRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(TicketRequests);
